import clsx from 'clsx';
import type { RxDocument } from 'rxdb';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Popover } from '@flintmint/web-shared/components/popover';

import { useCreateNoteFromTemplate, useNote, useNotePatch } from '@/hooks/rxdb-models';

import type { Note } from '@flintmint/core-shared/rxdb';
import type { NoteVariant } from '@/providers/rxdb/constants';

import styles from './template-actions.module.scss';

export const TemplateActions = (props: TemplateActionsProps) => {
  const { note, variant, templateId, onEditSourceTemplate } = props;

  const { data: template, isLoading: isTemplateLoading } = useNote(templateId);
  const { mutate: patchNote } = useNotePatch();
  const { mutate: createNoteFromTemplate } = useCreateNoteFromTemplate();

  const isSourceTemplateArchived = variant === 'notes' && template?.archived;
  const showMainButton = variant !== 'notes' || (!isTemplateLoading && !!template && !isSourceTemplateArchived);

  const config = {
    templates: {
      label: 'New Note',
      icon: 'keyframes-double-add',
      description: 'From Template',
      onClick: () => createNoteFromTemplate(note.id),
    },
    notes: {
      icon: 'edit-01',
      label: 'Edit Template',
      description: template?.name,
      onClick: () => !!template && onEditSourceTemplate?.(template),
    },
  };

  const menu = [
    {
      disabled: true,
      icon: 'menu-01',
      onClick: () => {},
      name: 'Update from Template',
    },
    {
      type: 'divider',
    },
    {
      danger: true,
      icon: 'file-remove',
      name: 'Detach From Template',
      onClick: () => patchNote(note, { templateId: null }),
    },
  ];

  const { label, icon, description, onClick } = config[variant];

  return (
    <div
      className={clsx(styles.templateActions, {
        [styles.templateEditBtn]: variant === 'notes',
      })}
    >
      {showMainButton && (
        <button onClick={onClick} aria-label={label}>
          <Icon type={icon} size={20} />
          <span className={styles.label}>
            <span>{label}</span>
            <span className="ellipsis-text" title={description}>
              {description}
            </span>
          </span>
        </button>
      )}
      {variant === 'notes' && (
        <Popover menu={menu} placement="bottom">
          <Icon type="more-horizontal" data-testid="menu" className={styles.menuIcon} />
        </Popover>
      )}
    </div>
  );
};

interface TemplateActionsProps {
  note: RxDocument<Note>;
  templateId?: string;
  variant: NoteVariant;
  onEditSourceTemplate?: (template: RxDocument<Note>) => void;
}
